<template>
    <div>
        <modal
            v-show="!showChild"
            class="ddModal"
            title="Dynamic Collection Options"
            header-bg-variant="blue-600"
            header-text-variant="white"
            :hide-header-close="true"
            @ok="onConfirm"
            @hide="onHide"
        >
            <div class="ddMapping" @click="onClickAway">
                <aside v-show="isQueryPromptVisible" class="ddMapping__prompt">
                    <div class="ddMapping__prompt-content">
                        <h5 class="ddMapping__prompt-title">
                            Confirm Action
                        </h5>

                        <p class="ddMapping__prompt-text">
                            Switching the query type will require you to remap your data. Are you sure you want to continue?
                        </p>

                        <footer class="ddMapping__prompt-footer">
                            <button
                                type="button"
                                class="ddMapping__prompt-button ddMapping__prompt-button--cancel"
                                @click="onCancelQueryType"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                class="ddMapping__prompt-button ddMapping__prompt-button--continue"
                                @click="onConfirmQueryType"
                            >
                                Continue
                            </button>
                        </footer>
                    </div>
                </aside>

                <article class="ddMapping__row ddMapping__mainPanel" :style="mainPanelStyles">
                    <div class="ddMapping__panel">
                        <vue-custom-scrollbar
                            ref="componentColumn"
                            tagname="section"
                            class="ddMapping__column ddMapping__column--component"
                            :settings="scrollSettings"
                            style="height: 100%;"
                        >
                            <h3 class="ddMapping__heading">
                                Component
                            </h3>

                            <div class="ddMapping__section">
                                <h4 class="ddMapping__label">
                                    SELECT A COMPONENT
                                </h4>

                                <toolbar-insert
                                    mode="click"
                                    toolbar="dynamic-mapping"
                                    :screen="screen"
                                    :overflow-scroll="false"
                                />
                            </div>
                        </vue-custom-scrollbar>

                        <section v-show="selectedComponentId" class="ddMapping__column ddMapping__column--next">
                            <button
                                type="button"
                                class="ddMapping__next"
                                @click="paneIndex = 1"
                            >
                                <font-awesome-icon :icon="['fal', 'chevron-circle-right']" />

                                <span class="ddMapping__next-text">
                                    Set Query
                                </span>
                            </button>
                        </section>
                    </div>

                    <vue-custom-scrollbar
                        class="ddMapping__panel"
                        :settings="scrollSettings"
                        style="height: 100%;"
                    >
                        <section class="ddMapping__column ddMapping__column--back">
                            <button
                                type="button"
                                class="ddMapping__back"
                                @click="paneIndex = 0"
                            >
                                <font-awesome-icon :icon="['fal', 'chevron-circle-left']" />
                            </button>
                        </section>

                        <vue-custom-scrollbar
                            ref="queryColumn"
                            tagname="section"
                            class="ddMapping__column ddMapping__column--query"
                            :settings="scrollSettings"
                            style="height: 100%;"
                        >
                            <h3 class="ddMapping__heading">
                                Query
                            </h3>

                            <div class="ddMapping__section">
                                <h4 class="ddMapping__label">
                                    SELECT A QUERY TYPE
                                </h4>

                                <dropdown
                                    :value="premadeQuery"
                                    :options="queryTypeOptions"
                                    @input="onPremadeQueryInput"
                                />
                            </div>

                            <div v-if="premadeQuery === 'item'" class="fc-display-contents">
                                <div class="ddMapping__section">
                                    <h4 class="ddMapping__label">
                                        DATASOURCE VALUE
                                    </h4>

                                    <input
                                        v-model="datasourceValue"
                                        type="text"
                                        class="ddMapping__input"
                                        @change="onDatasourceValueChanged"
                                    >

                                    <button class="ddMapping__button ddMapping__button--wide" @click="updateQuery">
                                        <span :style="{ visibility: isSearching ? 'hidden' : 'visible' }">
                                            QUERY
                                        </span>

                                        <loading-icon
                                            v-show="isSearching"
                                            color="#fff"
                                            class="ddMapping__button-loading"
                                        />
                                    </button>
                                </div>
                            </div>

                            <div v-if="premadeQuery === 'API'" class="fc-display-contents">
                                <div class="ddMapping__section ddMapping__section--multi">
                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            API ENDPOINT
                                        </h4>

                                        <dropdown
                                            :value="apiID"
                                            :options="apiOptions"
                                            @input="onApiDropdown"
                                        />
                                    </div>
                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            STARTING PATH

                                            <tooltip>
                                                Please use <a href="https://restfulapi.net/json-jsonpath/" target="_blank">JSONPath</a> for this field.
                                            </tooltip>
                                        </h4>

                                        <input
                                            v-model="apiStartingItem"
                                            type="text"
                                            class="ddMapping__input"
                                        >
                                    </div>
                                </div>
                                <div class="ddMapping__section">
                                    <h4 class="ddMapping__label">
                                        DATA DEFAULTS
                                        <tooltip>
                                            Default data to send to the API. Exact query string will overwrite the values. Lock the values to prevent this.
                                        </tooltip>
                                    </h4>
                                    <form
                                        ref="apiQueryStringDefaultsForm"
                                        class="name-value__content"
                                        @submit.prevent
                                    >
                                        <header class="name-value__header">
                                            <div class="name-value__col">
                                                <span class="name-value__label">Name</span>
                                            </div>

                                            <div class="name-value__col">
                                                <span class="name-value__label">Value</span>
                                            </div>
                                        </header>

                                        <ul class="name-value__list">
                                            <li v-for="(attribute, i) in apiQueryStringDefaults" :key="i" class="name-value__item">
                                                <div class="name-value__col">
                                                    <input
                                                        class="name-value__input"
                                                        type="text"
                                                        :value="attribute.name"
                                                        required
                                                        @input="(e) => onDefaultInputName(apiQueryStringDefaults, e, i)"
                                                    >
                                                </div>

                                                <div class="name-value__col">
                                                    <input
                                                        class="name-value__input"
                                                        type="text"
                                                        :value="attribute.value"
                                                        required
                                                        @input="(e) => onDefaultInputValue(apiQueryStringDefaults, e, i)"
                                                    >
                                                    <button
                                                        v-if="attribute.lock"
                                                        class="f-ml-0.5 f-w-1"
                                                        title="Click to unlock"
                                                        @click="(e) => onDefaultLockToggleClick(apiQueryStringDefaults, e, i)"
                                                    >
                                                        <font-awesome-icon
                                                            :icon="['fal', 'lock-alt']"
                                                        />
                                                    </button>
                                                    <button
                                                        v-else
                                                        class="f-ml-0.5 f-w-1"
                                                        title="Click to lock"
                                                        @click="(e) => onDefaultLockToggleClick(apiQueryStringDefaults, e, i)"
                                                    >
                                                        <font-awesome-icon
                                                            :icon="['fal', 'unlock']"
                                                        />
                                                    </button>
                                                    <button
                                                        class="name-value__delete"
                                                        title="Remove"
                                                        @click="deleteDefault(apiQueryStringDefaults, i)"
                                                    >
                                                        <font-awesome-icon
                                                            :icon="['fal', 'trash-alt']"
                                                        />
                                                    </button>
                                                </div>
                                            </li>
                                        </ul>

                                        <button
                                            class="name-value__add"
                                            type="button"
                                            @click="addDataDefault('apiQueryStringDefaultsForm', apiQueryStringDefaults)"
                                        >
                                            Add Default
                                        </button>
                                    </form>
                                </div>
                                <div class="ddMapping__section">
                                    <button
                                        class="ddMapping__button ddMapping__button--wide"
                                        @click="updateQuery"
                                    >
                                        <span :style="{ visibility: isSearching ? 'hidden' : 'visible' }">
                                            QUERY
                                        </span>

                                        <loading-icon
                                            v-show="isSearching"
                                            color="#fff"
                                            class="ddMapping__button-loading"
                                        />
                                    </button>
                                </div>
                            </div>

                            <div v-if="premadeQuery === 'search'" class="fc-display-contents">
                                <div class="ddMapping__section ddMapping__section--multi">
                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            SEARCH TERM
                                        </h4>

                                        <input
                                            v-model="searchTerm"
                                            type="text"
                                            class="ddMapping__input"
                                        >
                                    </div>

                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            CATEGORY
                                        </h4>

                                        <dropdown v-model="category" :options="categoryOptions" />
                                    </div>
                                </div>

                                <div class="ddMapping__section ddMapping__section--multi">
                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            TAGS
                                        </h4>

                                        <tags-editor
                                            v-model="tags"
                                            :create-new-enabled="false"
                                            @delete="onTagRemove"
                                        />
                                    </div>

                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            TEMPLATE NAME FILTER
                                        </h4>

                                        <input v-model="templateName" class="ddMapping__input">
                                    </div>
                                </div>

                                <div class="ddMapping__section ddMapping__section--multi">
                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            ROOT ITEM
                                        </h4>

                                        <input
                                            v-model="rootItem"
                                            class="ddMapping__input"
                                            placeholder="Root Item"
                                        >
                                    </div>

                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            DATE FROM
                                        </h4>

                                        <input
                                            v-model="dateFrom"
                                            type="text"
                                            placeholder="mm/dd/yyyy"
                                            class="ddMapping__input"
                                        >
                                    </div>
                                </div>

                                <div class="ddMapping__section ddMapping__section--multi">
                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            DATE TO
                                        </h4>

                                        <input
                                            v-model="dateTo"
                                            type="text"
                                            placeholder="mm/dd/yyyy"
                                            class="ddMapping__input"
                                        >
                                    </div>

                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            SORT BY
                                        </h4>

                                        <dropdown v-model="sortBy" :options="sortOptions" />
                                    </div>
                                </div>

                                <div class="ddMapping__section ddMapping__section--multi">
                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            PAGE SIZE
                                        </h4>

                                        <input
                                            v-model="pageSize"
                                            type="text"
                                            class="ddMapping__input"
                                        >
                                    </div>

                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            PAGES ONLY
                                        </h4>

                                        <toggle v-model="pageOnly" />
                                    </div>
                                </div>

                                <div class="ddMapping__section ddMapping__section--multi">
                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            Facet On
                                        </h4>

                                        <input
                                            v-model="facetOn"
                                            type="text"
                                            class="ddMapping__input"
                                        >
                                    </div>

                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            Group by
                                        </h4>

                                        <input
                                            v-model="groupBy"
                                            type="text"
                                            class="ddMapping__input"
                                        >
                                    </div>
                                </div>

                                <div class="ddMapping__section ddMapping__section--multi">
                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            IGNORE QUERY STRING
                                        </h4>

                                        <toggle v-model="ignoreQuerystring" />
                                    </div>
                                </div>

                                <button class="ddMapping__button ddMapping__button--wide" @click="updateQuery">
                                    <span :style="{ visibility: isSearching ? 'hidden' : 'visible' }">
                                        SEARCH
                                    </span>

                                    <loading-icon
                                        v-show="isSearching"
                                        color="#fff"
                                        class="ddMapping__button-loading"
                                    />
                                </button>
                            </div>

                            <div v-if="premadeQuery === 'locations'" class="fc-display-contents">
                                <div class="ddMapping__section ddMapping__section--multi">
                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            SEARCH TERM
                                        </h4>

                                        <input
                                            v-model="searchTerm"
                                            type="text"
                                            class="ddMapping__input"
                                        >
                                    </div>

                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            CATEGORY
                                        </h4>

                                        <dropdown v-model="category" :options="categoryOptions" />
                                    </div>
                                </div>

                                <div class="ddMapping__section ddMapping__section--multi">
                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            TAGS
                                        </h4>

                                        <tags-editor
                                            v-model="tags"
                                            :create-new-enabled="false"
                                            @delete="onTagRemove"
                                        />
                                    </div>
                                    <div class="ddMapping__section-column" />
                                </div>

                                <div class="ddMapping__section ddMapping__section--multi">
                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            ZIP CODE
                                        </h4>

                                        <input
                                            v-model="zipCode"
                                            type="text"
                                            class="ddMapping__input"
                                        >
                                    </div>

                                    <div class="ddMapping__section-column">
                                        <h4 class="ddMapping__label">
                                            SEARCH RADIUS
                                        </h4>

                                        <input
                                            v-model="radius"
                                            type="text"
                                            class="ddMapping__input"
                                        >
                                    </div>
                                </div>

                                <button class="ddMapping__button ddMapping__button--wide" @click="updateQuery">
                                    <span :style="{ visibility: isSearching ? 'hidden' : 'visible' }">
                                        SEARCH
                                    </span>

                                    <loading-icon
                                        v-show="isSearching"
                                        color="#fff"
                                        class="ddMapping__button-loading"
                                    />
                                </button>
                            </div>

                            <div v-if="premadeQuery === 'manual'" class="fc-display-contents">
                                <manual-item-selection-option v-model="selectedItems" :is-searching="isSearching" @search="updateQuery" />
                            </div>

                            <div v-if="premadeQuery === 'graphQL'" class="fc-display-contents">
                                <div class="ddMapping__section">
                                    <h4 class="ddMapping__label">
                                        GraphQL
                                        <tooltip>
                                            Use the <a :href="graphQLUiUrl" target="_blank">Sitecore GraphQL UI</a> to build the query
                                        </tooltip>
                                    </h4>

                                    <textarea
                                        v-model="graphQL"
                                        class="ddMapping__input ddMapping__textarea graphql"
                                        spellcheck="false"
                                        @change="onGraphQLValueChanged"
                                    />

                                    <h4 class="ddMapping__label">
                                        Query variables
                                        <tooltip>
                                            Query variables in JSON format to pass to the above GraphQL query. Use $$contextItem$$ token to pass in the ID of the page context item.
                                        </tooltip>
                                    </h4>
                                    <textarea
                                        v-model="graphQLVariables"
                                        class="ddMapping__input ddMapping__textarea graphql-variables"
                                        spellcheck="false"
                                        @change="onGraphQLVariablesValueChanged"
                                    />

                                    <h4 class="ddMapping__label">
                                        GraphQL Result Data Format
                                        <tooltip>
                                            Please use <a href="https://restfulapi.net/json-jsonpath/" target="_blank">JSONPath</a> to convert the GraphQL data result format to JSON. As an example - {"prop":"value", "prop1":"value1"}
                                        </tooltip>
                                    </h4>

                                    <input
                                        v-model="graphQLResultFormat"
                                        type="text"
                                        class="ddMapping__input graphql-resultFormat"
                                    >

                                    <div class="ddMapping__section">
                                        <h4 class="ddMapping__label">
                                            TOKEN DEFAULTS
                                            <tooltip>
                                                Default values for tokens. Useful in instances where context item is only available on page and we need to provide a value for a query e.g. this screen.
                                            </tooltip>
                                        </h4>
                                        <form
                                            ref="graphQLVariableDefaultForm"
                                            class="name-value__content"
                                            @submit.prevent
                                        >
                                            <header class="name-value__header">
                                                <div class="name-value__col">
                                                    <span class="name-value__label">Token</span>
                                                </div>

                                                <div class="name-value__col">
                                                    <span class="name-value__label">Value</span>
                                                </div>
                                            </header>

                                            <ul class="name-value__list">
                                                <li v-for="(attribute, i) in graphQLVariableDefaults" :key="i" class="name-value__item">
                                                    <div class="name-value__col">
                                                        <input
                                                            class="name-value__input"
                                                            type="text"
                                                            :value="attribute.name"
                                                            required
                                                            @input="(e) => onDefaultInputName(graphQLVariableDefaults, e, i)"
                                                        >
                                                    </div>

                                                    <div class="name-value__col">
                                                        <input
                                                            class="name-value__input"
                                                            type="text"
                                                            :value="attribute.value"
                                                            required
                                                            @input="(e) => onDefaultInputValue(graphQLVariableDefaults, e, i)"
                                                        >
                                                        <button
                                                            class="name-value__delete"
                                                            title="Remove"
                                                            @click="deleteDefault(graphQLVariableDefaults, i)"
                                                        >
                                                            <font-awesome-icon
                                                                :icon="['fal', 'trash-alt']"
                                                            />
                                                        </button>
                                                    </div>
                                                </li>
                                            </ul>

                                            <button
                                                class="name-value__add"
                                                type="button"
                                                @click="addDataDefault('graphQLVariableDefaultForm', graphQLVariableDefaults)"
                                            >
                                                Add Default
                                            </button>
                                        </form>
                                    </div>

                                    <div class="ddMapping__section ddMapping__section--multi">
                                        <div class="ddMapping__section-column">
                                            <h4 class="ddMapping__label">
                                                IGNORE QUERY STRING
                                            </h4>

                                            <toggle v-model="ignoreQuerystring" />
                                        </div>
                                    </div>

                                    <button class="ddMapping__button ddMapping__button--wide" @click="onGraphQLQueryClick">
                                        <span :style="{ visibility: isSearching ? 'hidden' : 'visible' }">
                                            QUERY
                                        </span>

                                        <loading-icon
                                            v-show="isSearching"
                                            color="#fff"
                                            class="ddMapping__button-loading"
                                        />
                                    </button>
                                </div>
                            </div>
                        </vue-custom-scrollbar>

                        <section
                            ref="resultColumn"
                            class="ddMapping__column ddMapping__column--result"
                        >
                            <div v-show="dataObject" class="fc-display-contents">
                                <h3 class="ddMapping__heading">
                                    Result
                                </h3>

                                <div class="ddMapping__section">
                                    <h4 class="ddMapping__label">
                                        SAMPLE DATA
                                    </h4>
                                    <vue-custom-scrollbar
                                        tagname="pre"
                                        class="ddMapping__sample"
                                        :settings="scrollSettings"
                                        style="height: 60vh;"
                                    >
                                        {{ truncatedDataObject }}
                                    </vue-custom-scrollbar>
                                </div>
                            </div>
                        </section>

                        <section class="ddMapping__column ddMapping__column--next">
                            <div v-show="dataObject" class="fc-display-contents">
                                <button
                                    type="button"
                                    class="ddMapping__next"
                                    @click="paneIndex = 2"
                                >
                                    <font-awesome-icon :icon="['fal', 'chevron-circle-right']" />

                                    <span class="ddMapping__next-text">
                                        Set Mapping
                                    </span>
                                </button>
                            </div>
                        </section>
                    </vue-custom-scrollbar>

                    <div class="ddMapping__panel">
                        <section class="ddMapping__column ddMapping__column--back">
                            <button
                                type="button"
                                class="ddMapping__back"
                                @click="paneIndex = 1"
                            >
                                <font-awesome-icon :icon="['fal', 'chevron-circle-left']" />
                            </button>
                        </section>

                        <section
                            ref="mappingColumn"
                            class="ddMapping__column ddMapping__column--mapping"
                            style="height: 100%;"
                        >
                            <div v-if="premadeQuery" class="fc-display-contents">
                                <h3 class="ddMapping__heading">
                                    Data Mapping - {{ selectedComponentName }}
                                </h3>

                                <div
                                    v-show="!transformMode"
                                    class="ddMapping__section"
                                >
                                    <div class="ddMapping__field">
                                        <div class="ddMapping__field-column">
                                            <h4 class="ddMapping__label">
                                                COMPONENT FIELD
                                            </h4>
                                        </div>
                                    </div>
                                    <div class="ddMapping__field">
                                        <div class="ddMapping__field-column">
                                            <dropdown
                                                placeholder="Select One"
                                                :value="selectedAtomIndex"
                                                :options="atomCollectionOptions"
                                                :max-list-height="200"
                                                @input="onAtomSelect"
                                            />
                                        </div>
                                        <div class="ddMapping__field-column">
                                            <button
                                                class="ddMapping__button"
                                                :disabled="selectedAtomIndex < 0"
                                                @click="toggleTransformMode"
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                    <div class="ddMapping__field">
                                        <div class="ddMapping__field-column">
                                            <h4 class="ddMapping__label">
                                                ACTIVE MAPPINGS
                                            </h4>
                                        </div>
                                    </div>
                                    <div class="ddMapping__active-mappings">
                                        <span
                                            v-if="!atomCollectionTransforms.length"
                                            class="f-italic"
                                        >
                                            { empty }
                                        </span>
                                        <div v-else>
                                            <div
                                                v-for="(item, index) in atomCollectionTransforms"
                                                :key="index"
                                            >
                                                <button
                                                    class="ddMapping__button ddMapping__button--alt f-m-0.5 f-rounded-0.375 f-w-1/3"
                                                    @click="selectAtom(item)"
                                                >
                                                    {{ item.displayName }}
                                                </button>
                                                <button
                                                    class="ddMapping__button f-m-0.5"
                                                    @click="onMappingEdit(item)"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    class="ddMapping__button f-m-0.5"
                                                    @click="onRemoveMapping(item)"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-show="transformMode"
                                    class="ddMapping__section"
                                >
                                    <h4 class="ddMapping__label">
                                        TRANSFORM ({{ selectedAtomDisplayName }}) - use $data for the root item:
                                    </h4>
                                    <div class="ddMapping__transform">
                                        <rich-html-editor
                                            ref="codeEditor"
                                            mode="javascript"
                                            theme="code"
                                            :input="editorCode"
                                            :auto-complete-words="autoCompleteWords"
                                            @change="onEditorCodeChange"
                                        />
                                    </div>
                                    <vue-custom-scrollbar
                                        tagname="section"
                                        class="ddMapping__sample"
                                        style="height: 28vh;"
                                        :settings="scrollSettings"
                                    >
                                        <json-clickable
                                            :data="truncatedDataObject"
                                        />
                                    </vue-custom-scrollbar>

                                    <div class="ddMapping__transform-footer">
                                        <button
                                            class="ddMapping__button ddMapping__button--alt"
                                            @click="toggleTransformMode"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            v-if="!selectedAtomIsDynamicCollection"
                                            class="ddMapping__button"
                                            @click="applyTransform"
                                        >
                                            Apply
                                        </button>
                                        <button
                                            v-else
                                            class="ddMapping__button"
                                            @click="openNested"
                                        >
                                            Map
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </article>

                <div class="ddMapping__bar" :style="barStyles">
                    <button v-show="selectedComponentId" class="ddMapping__bar-handle" @click="onBarClick">
                        <font-awesome-icon :icon="['fas', barCaret]" />
                    </button>
                </div>

                <figure class="ddMapping__row ddMapping__preview" :style="previewPanelStyles">
                    <vue-custom-scrollbar
                        class="ddMapping__column ddMapping__column--preview"
                        :settings="scrollSettings"
                        style="height: 100%;"
                    >
                        <loading-component v-show="isLoading" />

                        <component
                            :is="selectedComponent.elementType"
                            v-if="selectedComponent && selectedComponent.elementType"
                            :key="componentUpdateTrigger"
                            class="ddMapping__preview-component"
                            :class="{ 'ddMapping--disable-pointer': paneIndex != 2 }"
                            :pointer="1"
                            :index="0"
                            v-bind="selectedComponent"
                        />
                    </vue-custom-scrollbar>
                </figure>
            </div>

            <template v-slot:modal-footer>
                <button class="flex-modal__button flex-modal__button--cancel" @click="onCancel">
                    Cancel
                </button>

                <button class="flex-modal__button flex-modal__button--continue" @click="onConfirm">
                    Update
                </button>
            </template>
        </modal>
        <modal-dynamic-options-nested
            v-if="showChild"
            :is-child="true"
            :component-id="childComponentId"
            :collection-data="childData"
            :dynamic-data-mapper="childMapping"
            :parent-breadcrumbs="breadcrumbItems"
            @child-update="onChildUpdated"
            @child-cancel="onChildCancel"
            @breadcrumb="onChildBreadcrumbClick"
        />
    </div>
</template>

<script>
import gql from 'graphql-tag';
import { mapMutations, mapState } from 'vuex';
import vueCustomScrollbar from 'vue-custom-scrollbar';
import 'vue-custom-scrollbar/dist/vueScrollbar.css';

import LoadingComponent from '@/components/shared/loading/loading-component';
import LoadingIcon from '@/components/shared/loading/loading-icon';
import ToolbarInsert from '@/components/shared/toolbars/toolbar-insert';
import TagsEditor from '@/components/utilities/tags-editor';
import { SET_DYNAMIC_MAP_PREVIEW_MODE } from '@/store/types/mutation-types';

import mapFilters from '@/utils/map-filters';
import listenOnRootMixin from '@/mixins/listen-on-root';
import RichHtmlEditor from '@/components/shared/modal/rich-html-editor';
import JsonClickable from '@/components/utilities/json-clickable';
import ManualItemSelectionOption from '@/components/component-editor/dynamic-collection/manual-item-selection-option';
import ModalDynamicOptionsBase from '@/mixins/modal-dynamic-options-base';
import ModalDynamicOptionsNested from '@/components/component-editor/modal-dynamic-options-nested';

// each map key is the local variable name
const searchDataMap = new Map([
    ['atomCollection', {
        customAttributeName: 'dynamicDataMapper',
        defaultValue: [],
    }],

    ['apiID', {
        customAttributeName: 'apiIDDynamic',
        defaultValue: '',
    }],

    ['apiStartingItem', {
        customAttributeName: 'apiStartingItemDynamic',
        defaultValue: '$.data',
    }],

    ['apiQueryStringDefaults', {
        customAttributeName: 'apiQueryStringDefaultsDynamic',
        defaultValue: [{ name: '', value: '', lock: false }],
    }],

    ['category', {
        customAttributeName: 'categoryTermDynamic',
        defaultValue: '',
        manualDefaultValue: '',
    }],

    ['datasourceValue', {
        customAttributeName: 'dynamicDatasourceValue',
        defaultValue: '',
    }],

    ['dateFrom', {
        customAttributeName: 'dateFromTermDynamic',
        defaultValue: '',
        manualDefaultValue: '',
    }],

    ['dateTo', {
        customAttributeName: 'dateToTermDynamic',
        defaultValue: '',
        manualDefaultValue: '',
    }],

    ['page', {
        customAttributeName: 'pageTermDynamic',
        defaultValue: 0,
    }],

    ['pageOnly', {
        customAttributeName: 'pageOnlyTermDynamic',
        defaultValue: true,
        manualDefaultValue: false,
    }],

    ['ignoreQuerystring', {
        customAttributeName: 'ignoreQuerystringTermDynamic',
        defaultValue: true,
    }],

    ['pageSize', {
        customAttributeName: 'pageSizeTermDynamic',
        defaultValue: 10,
        manualDefaultValue: 500,
    }],

    ['premadeQuery', {
        customAttributeName: 'premadeQuery',
        defaultValue: '',
    }],

    ['rootItem', {
        customAttributeName: 'rootitemTermDynamic',
        defaultValue: '/sitecore/content',
    }],

    ['searchTerm', {
        customAttributeName: 'queryTermDynamic',
        defaultValue: '',
    }],

    ['selectedComponentId', {
        customAttributeName: 'selectedPremadeComponentId',
        defaultValue: '',
    }],

    ['sortBy', {
        customAttributeName: 'sortByTermDynamic',
        defaultValue: 'B3A0F95291C848E6B040B9C03614D133',
        manualDefaultValue: '',
    }],

    ['tags', {
        customAttributeName: 'tagsTermDynamic',
        defaultValue: [],
    }],

    ['templateName', {
        customAttributeName: 'templateNameTermDynamic',
        defaultValue: '',
    }],

    ['zipCode', {
        customAttributeName: 'zipCodeTermDynamic',
        defaultValue: '',
    }],

    ['radius', {
        customAttributeName: 'radiusTermDynamic',
        defaultValue: 10,
    }],

    ['facetOn', {
        customAttributeName: 'facetOnDynamic',
        defaultValue: 'category,tags,tags with categories,gender,_templatename',
        manualDefaultValue: '',
    }],

    ['groupBy', {
        customAttributeName: 'groupByDynamic',
        defaultValue: '',
    }],

    ['graphQL', {
        customAttributeName: 'dynamicGraphQL',
        defaultValue: '',
    }],

    ['graphQLVariables', {
        customAttributeName: 'dynamicGraphQLVariables',
        defaultValue: '',
    }],

    ['graphQLResultFormat', {
        customAttributeName: 'dynamicGraphQLResultFormat',
        defaultValue: '$.data',
    }],


    ['graphQLVariableDefaults', {
        customAttributeName: 'dynamicGraphQLVariableDefaults',
        defaultValue: [{ name: '', value: '', lock: false }],
    }],

    ['selectedItems', {
        customAttributeName: 'selectedItemsTermDynamic',
        defaultValue: [],
    }],
]);

export default {
    name: 'modal-dynamic-options',
    components: {
        LoadingComponent,
        LoadingIcon,
        ToolbarInsert,
        vueCustomScrollbar,
        TagsEditor,
        RichHtmlEditor,
        JsonClickable,
        ManualItemSelectionOption,
        ModalDynamicOptionsNested,
    },

    mixins: [ModalDynamicOptionsBase, listenOnRootMixin],

    apollo: {
        sortOptionData: {
            query: gql`
                {
                    item(path: "/sitecore/system/Settings/Foundation/Search/Sort Options") {
                        children {
                            id
                            name
                        }
                    }
                }
            `,

            update(data) {
                return data.item.children;
            },
        },

        categoryOptionData: {
            query: gql`
                {
                    item(path: "/sitecore/content/Global Settings/Categories") {
                        children {
                            id
                            name
                        }
                    }
                }
            `,

            update(data) {
                return data.item.children;
            },
        },

        externalData: {
            query: gql`
                {
                    item(path: "/sitecore/content/Data/External APIs") {
                        children {
                            id
                            name
                        }
                    }
                }
            `,
            update(data) {
                return data.item.children;
            },
        },
    },

    data() {
        return {
            query: '',

            queryTypeOptions: [
                { text: 'Search', value: 'search' },
                { text: 'Item', value: 'item' },
                { text: 'API', value: 'API' },
                { text: 'Locations', value: 'locations' },
                { text: 'Manual Item Selection', value: 'manual' },
                { text: 'GraphQL', value: 'graphQL' },
            ],

            tagKeywords: '',
            selectedItems: [],

            // external api
            apiID: '',
            apiStartingItem: '',
            apiMockQueryString: '',
            apiQueryStringDefaults: [
                { name: '', value: '', lock: false },
            ],

            // UI state
            isQueryPromptVisible: false,
            isSearching: false,

            // saved search data
            category: '',
            datasourceValue: '',
            dateFrom: '',
            dateTo: '',
            page: 0,
            pageOnly: true,
            pageSize: 10,
            premadeQuery: '',
            rootItem: '/sitecore/content',
            searchTerm: '',
            selectedComponentId: '',
            sortBy: 'B3A0F95291C848E6B040B9C03614D133',
            tags: [],
            templateName: '',
            radius: 10,
            zipCode: '',
            ignoreQuerystring: true,

            facetOn: 'category,tags,tags with categories,gender,_templatename',
            groupBy: '',

            graphQL: '',
            graphQLVariables: '',
            graphQLResultFormat: '',
            graphQLVariableDefaults: [
                { name: '', value: '', lock: false },
            ],
        };
    },

    computed: {
        ...mapState({
            collectionData: state => state.collectionData,
            selectedElements: state => state.page.selectedElements,
        }),

        // styles - BEGIN

        // available (percentage) widths for the preview component window
        previewWidth() {
            switch (this.paneIndex) {
                case 0:
                    return { collapsed: 30, expanded: 50 };

                case 1:
                    return { collapsed: 0, expanded: 30 };

                case 2:
                    return { collapsed: 30, expanded: 50 };

                default:
                    console.warn(`Invalid pane index "${ this.paneIndex }" detected.`);
                    return { collapsed: 30, expanded: 50 };
            }
        },

        mainPanelStyles() {
            let transform;
            let width = '100%';

            if (this.selectedComponentId) {
                width = this.isPreviewExpanded
                    ? `${ 100 - this.previewWidth.expanded }%`
                    : `${ 100 - this.previewWidth.collapsed }%`;
            }

            switch (this.paneIndex) {
                case 0:
                    transform = 'translateX(0%)';
                    break;

                case 1:
                    transform = 'translateX(-100%)';
                    break;

                case 2:
                    transform = 'translateX(-200%)';
                    break;

                default:
                    console.warn(`Cannot switch to pane index ${ this.paneIndex }, as it does not exist.`);
            }

            return {
                width,
                transform,
            };
        },


        // styles - END

        // dropdown options - BEGIN

        apiOptions() {
            if (!this.externalData) return [];

            const options = this.externalData.map((data) => {
                return {
                    text: data.name,
                    value: data.id,
                };
            });

            options.unshift({
                text: 'None',
                value: '',
            });

            return options;
        },

        categoryOptions() {
            if (!this.categoryOptionData) return [];

            const options = this.categoryOptionData.map((data) => {
                return {
                    text: data.name,
                    value: data.id,
                };
            });

            options.unshift({
                text: 'None',
                value: '',
            });

            return options;
        },

        sortOptions() {
            if (!this.sortOptionData) return [];

            return this.sortOptionData.map((data) => {
                return {
                    text: data.name,
                    value: data.id,
                };
            });
        },

        // dropdown options - END

        searchObject() {
            if (this.selectedItems && this.selectedItems.length > 0) {
                return {
                    rootItem: '',
                    searchTerm: '',
                    tags: '',
                    category: '',
                    templateName: '',
                    dateFrom: '',
                    dateTo: '',
                    pageSize: 500,
                    sortBy: '',
                    pageOnly: false,
                    zipCode: '',
                    radius: this.radius,
                    selectedItems: this.selectedItems,
                    ignoreQuerystring: true,
                    facetOn: [],
                    groupBy: '',
                };
            }
            return {
                rootItem: this.rootItem,
                searchTerm: this.searchTerm,
                tags: this.tagIds,
                category: this.category,
                templateName: this.templateName,
                dateFrom: this.dateFrom,
                dateTo: this.dateTo,
                pageSize: this.pageSize,
                sortBy: this.sortBy,
                pageOnly: this.pageOnly,
                zipCode: this.zipCode,
                radius: this.radius,
                selectedItems: [],
                ignoreQuerystring: this.ignoreQuerystring,
                facetOn: this.facetOn
                    ? this.facetOn.split(',').map(i => i.trim())
                    : [],
                groupBy: this.groupBy,
            };
        },

        tagIds() {
            // TAGID|TAGID|TAGID (will do an AND filter)
            return this.tags.map(t => t.id).join('|');
        },

        graphQLObject() {
            return {
                query: this.graphQL,
                variables: this.graphQLVariables,
                graphQLJsonPath: this.graphQLResultFormat,
                tokenDefaults: this.graphQLVariableDefaults.filter((i) => { return i.name !== ''; }),
                ignoreQuerystring: this.ignoreQuerystring,
            };
        },

        graphQLUiUrl() {
            const index = process.env.VUE_APP_GRAPHQL_HTTP.indexOf('?');
            return `${ process.env.VUE_APP_GRAPHQL_HTTP.substr(0, index) }/ui${ process.env.VUE_APP_GRAPHQL_HTTP.substr(index) } `;
        },

    },

    watch: {
        collectionData: {
            handler() {
                if (this.collectionData) this.processNewObject();
            },
            immediate: true,
        },

        paneIndex() {
            this.$refs.componentColumn.$el.scrollTo(0, 0);
            this.$refs.queryColumn.$el.scrollTo(0, 0);
            this.$refs.resultColumn.scrollTo(0, 0);
            this.$refs.mappingColumn.scrollTo(0, 0);
        },

        selectedElements() {
            this.onSelectedElement();
        },
    },

    created() {
        this.setDynamicMapPreviewMode(true);
        this.init();

        this.listenOnRoot('flex::collection::fetch-complete', () => {
            this.isSearching = false;
        });

        this.listenOnRoot('flex::json::click', this.insertIntoEditorCode);
        this.listenOnRoot('flex::dynamicMap::click', this.handlePreviewAtomClick);
        this.listenOnRoot('flex::dynamicMap::doubleClick', this.handlePreviewAtomDoubleClick);
    },

    methods: {
        ...mapFilters(['titleCase']),

        ...mapMutations({
            setDynamicMapPreviewMode: SET_DYNAMIC_MAP_PREVIEW_MODE,
        }),

        init() {
            this.listenOnRoot('flex::component::select', this.onComponentSelect);

            // need to check for the existence of the element first
            // ... as this method also fires when elements are deleted and subsequently de-selected
            const isDynamicCollection = this.selectedElements[0].elementType === 'collection-dynamic';

            if (isDynamicCollection) {
                this.loadData();
                this.loadComponent();
            }
        },

        onApiDropdown(value) {
            this.apiID = value;
        },

        onHide() {
            this.paneIndex = 0;
        },

        onPremadeQueryInput(premadeQueryNew) {
            // prompt the user before they change the "premadeQuery" value (if search data exists)
            // ... as doing so will reset the data
            if ((this.dataObject || this.atomCollectionTransforms?.length)
                && this.premadeQuery !== premadeQueryNew) {
                this.isQueryPromptVisible = true;
            } else {
                this.selectedElements[0].setElementData('premadeQuery', this.premadeQuery);
            }

            this.premadeQuery = premadeQueryNew;
        },

        onConfirmQueryType() {
            this.selectedElements[0].setElementData('premadeQuery', this.premadeQuery);

            // reset the existing data
            const removeData = prop => this.selectedElements[0].removeElementData(prop);

            for (const [varName, data] of searchDataMap) {
                const ignoreVars = ['premadeQuery', 'selectedComponentId', 'atomCollection'];

                if (!ignoreVars.includes(varName)) {
                    removeData(data.customAttributeName);
                    if (this.premadeQuery === 'manual' && data.manualDefaultValue !== undefined) {
                        this[varName] = data.manualDefaultValue;
                    } else {
                        this[varName] = data.defaultValue;
                    }
                }
            }

            this.dataObject = null;
            this.isQueryPromptVisible = false;
            this.setChangedData(true);
            this.clearAtomCollectionTransforms();
        },

        onCancelQueryType() {
            // switch the dropdown back to the previous value
            this.premadeQuery = this.premadeQuery === 'search' ? 'item' : 'search';
            this.isQueryPromptVisible = false;
        },

        onDatasourceValueChanged() {
            this.selectedElements[0].setElementData('dynamicDatasourceValue', this.datasourceValue);
        },

        onGraphQLValueChanged() {
            this.selectedElements[0].setElementData('dynamicGraphQL', this.graphQL);
        },

        onGraphQLVariablesValueChanged() {
            this.selectedElements[0].setElementData('dynamicGraphQLVariables', this.graphQLVariables);
        },

        onGraphQLResultDataFormatChanged() {
            this.selectedElements[0].setElementData('dynamicGraphQLResultFormat', this.graphQLResultFormat);
        },

        onConfirm() {
            this.updateData();
            this.hideModalBase();
        },

        onCancel() {
            this.init();
            this.hideModalBase();
        },

        hideModalBase() {
            this.hideModal();
            this.paneIndex = 0;
            this.setDynamicMapPreviewMode(false);
        },

        onTagRemove(tagIndex) {
            this.tags.splice(tagIndex, 1);
        },

        loadData() {
            const hasData = prop => this.selectedElements[0].hasElementData(prop);
            const getData = prop => this.selectedElements[0].getElementData(prop);
            for (const [varName, data] of searchDataMap) {
                if (hasData(data.customAttributeName)) this[varName] = getData(data.customAttributeName);
            }
        },

        flexComponentProcessor(component, currentPath = '$') {
            this.grabElements(component, currentPath);

            if (component.elements && component.elements.length > 0) {
                component.elements.forEach((element) => {
                    const newPath = `${ currentPath }.elements[${ element.pointer - 1 }]`;
                    this.flexComponentProcessor(element, newPath);
                });
            }
        },

        updateQuery() {
            this.isSearching = true;
            this.updateData();
        },

        updateData() {
            const setData = (prop, value) => this.selectedElements[0].setElementData(prop, value);

            for (const [varName, data] of searchDataMap) {
                setData(data.customAttributeName, this[varName]);
            }

            // update the collection-dynamic component
            this.$root.$emit(
                'flex::collection::set-collection-data',
                this.selectedElements[0].id,
                this.selectedComponentId,
                this.premadeQuery,
                this.searchObject,
                this.apiQueryStringDefaults.filter((i) => { return i.name !== ''; }),
                this.graphQLObject,
            );

            this.setChangedData(true);
        },

        processNewObject() {
            this.objectProperties = [];
            this.isSearching = false;
            this.dataObject = { ...this.collectionData };
        },

        onDefaultInputName(defaultVariable, event, index) {
            defaultVariable[index].name = event.target.value;
        },

        onDefaultInputValue(defaultVariable, event, index) {
            defaultVariable[index].value = event.target.value;
        },

        onDefaultLockToggleClick(defaultVariable, event, index) {
            defaultVariable[index].lock = !this.apiQueryStringDefaults[index].lock;
        },

        deleteDefault(defaultVariable, index) {
            defaultVariable.splice(index, 1);
        },

        addDataDefault(ref, defaultVariable) {
            if (!this.$refs[ref].reportValidity()) {
                return;
            }

            defaultVariable.push({ name: '', value: '', lock: false });
        },

        onGraphQLQueryClick() {
            this.updateQuery();
        },
    },
};
</script>

<style lang="scss">
    @import '@/styles/_includes.scss';

    .ddModal {
        .modal-dialog {
            width: 100%;
            height: 100%;
            max-width: 90%;
            max-height: 90%;
        }

        .modal-content {
            height: 100%;
        }

        .modal-body {
            overflow: hidden;
        }
    }

    .ddMapping {
        $this: &;
        height: 100%;
        overflow: hidden;
        position: relative;

        &__row {
            position: relative;
            display: flex;
            transition: transform 0.35s, width 0.35s;
        }

        &__panel {
            display: flex;
            min-width: 100%;
        }

        &__column {
            $column-padding: 2em;

            position: relative;
            padding: $column-padding;
            flex-grow: 1;
            flex-basis: 0;

            &:first-of-type {
                padding-left: $column-padding * 2;
            }

            &:last-of-type {
                padding-right: $column-padding * 2;
            }

            &--component {
                max-width: 32rem;
            }

            &--query {
                flex-grow: 3;
            }

            &--result {
                flex-grow: 3;
            }

            &--next,
            &--back {
                display: flex;
                justify-content: center;
            }

            &--next {
                align-items: center;
            }

            &--back {
                max-width: 6rem;
                align-items: flex-start;
                padding-top: 3em;
            }
        }

        &__next,
        &__back {
            display: flex;
            flex-direction: column;
            align-items: center;
            color: var(--app-linkColor);
            font-size: 4rem;

            &:hover {
                color: var(--app-linkHoverColor);
            }
        }

        &__next-text {
            opacity: 0.85;
            font-size: 0.25em;
            margin-top: 0.75em;
        }

        &__back {
            font-size: 2rem;
        }

        &__mainPanel {
            height: 100%;
        }

        &__preview {
            background-color: white;
            color: black;
            position: absolute;
            right: 0;
            top: 0;
            height: 100%;
            width: 50%;

            .drop-zone {
                display: none;
            }
        }

        &--disable-pointer {
            pointer-events: none;
        }

        &__heading {
            display: flex;
            align-items: center;
            font-size: 1rem;
            font-weight: 600;
            margin-bottom: 2em;
        }

        &__section {
            padding-bottom: 1.5rem;

            &--multi {
                display: flex;
            }
        }

        &__section-column {
            width: 50%;

            &:first-child { padding-right: 1em; }
            &:last-child { padding-left: 1em; }
        }

        &__label {
            display: flex;
            width: 100%;
            margin-bottom: 0.5rem;
            color: var(--form-labelColor);
            font-size: 0.75rem;
            font-weight: 500;
        }

        &__input {
            color: var(--form-inputColor);
            background-color: var(--form-inputBackgroundColor);
            border: 1px solid var(--form-inputBorderColor);
            font-size: 0.875rem;
            width: 100%;
            max-width: 28em;
            padding: 0.5rem 0.75rem;

            &.graphql-resultFormat{
                height: 4rem;
                max-width: none;
                margin-bottom: 1rem;
            }
        }

        &__button {
            position: relative;
            display: inline-block;
            color: var(--form-buttonColor);
            background-color: var(--form-buttonBackgroundColor);
            border-radius: 4px;
            font-size: 0.875rem;
            padding: 0.75em 3em;
            font-weight: 600;

            &:hover {
                color: var(--form-buttonHoverColor);
                background-color: var(--form-buttonHoverBackgroundColor);
            }

            &[disabled] {
                color: var(--form-altButtonColor);
                background-color: var(--form-altButtonBackgroundColor);
            }
        }

        &__button--wide {
            margin-top: 3em;
            min-width: 100%;
        }

        &__button--small {
            margin: 0.5em;
            min-width: auto;
        }

        &__button--alt {
            color: var(--form-altButtonColor);
            background-color: var(--form-altButtonBackgroundColor);

            &:hover {
                color: var(--form-altButtonHoverColor);
                background-color: var(--form-altButtonHoverBackgroundColor);
            }
        }

        &__button-loading {
            @extend %center-middle;
            margin-top: 3px;
        }

        &__sample {
            text-align: left;
            white-space: pre-wrap;
            word-break: break-word;
            font-family: monospace;
            font-size: 1.25rem;
            color: $code-snippet-color;
            background-color: $code-snippet-background;
            padding: 1em;
        }

        &__field {
            display: flex;

            &:not(:last-of-type) {
                padding-bottom: 1.5rem;
            }
        }

        &__field-column {
            width: 25%;

            &:not(:first-of-type) {
                padding-left: 2rem;
            }

            &:not(:last-of-type) {
                padding-right: 2rem;
            }

            &-wide {
                width:50%;
            }
        }

        &__field-name {
            width: 100%;
            padding: 0.5em;
            text-align: center;
            color: var(--form-inputColor);
            background-color: var(--form-inputBackgroundColor);
            border: 3px solid var(--form-inputBorderColor);
            font-size: 0.8125rem;

            &:hover {
                color: var(--form-inputHoverColor);
                background-color: var(--form-inputHoverBackgroundColor);
            }

            &.#{ $is-selected } {
                border-color: var(--form-controlColor);
            }
        }

        &__field-preview {
            display: inline-block;
            vertical-align: middle;
            padding: 0.75em 1.25em;
            color: $code-snippet-color;
            background-color: $code-snippet-background;
            font-family: monospace;
            font-size: 0.875rem;
            overflow: hidden;
            height:200px;
            width: 100%;
        }

        &__prompt {
            @extend %fill;
            background-color: rgba(black, 0.94);
            z-index: 50;
        }

        &__prompt-content {
            @extend %center-middle;
            width: 33em;
            background-color: var(--modal-backgroundColor);
        }

        &__prompt-title {
            background-image: linear-gradient(var(--modal-headerBackgroundColorTop), var(--modal-headerBackgroundColorBottom));
            color: var(--modal-headerColor);
            border-bottom: 1px solid var(--form-buttonBorderColor);
            padding: 1em;
            font-weight: 600;
        }

        &__prompt-text {
            font-size: 1rem;
            padding: 2em;
            line-height: 1.5em;
        }

        &__prompt-footer {
            display: flex;
            justify-content: flex-end;
            padding: 1em;
            border-top: 1px solid var(--form-buttonBorderColor);
            background-color: var(--modal-headerBackgroundColorBottom);
        }

        &__prompt-button {
            &--cancel {
                @extend %button-alt;
                margin-right: 1em;
            }

            &--continue {
                @extend %button;
            }
        }

        &__bar {
            @extend %center;
            z-index: 10;
            height: 100%;
            background-color: var(--sidebar-handleBackgroundColor);
            border-left: 1px solid var(--sidebar-handleBorderColor);
            width: 5px;
            top: 0;
            transition: left 0.35s;
        }

        &__bar-handle {
            @extend %middle;
            right: 100%;
            color: var(--sidebar-handleColor);
            background-color: var(--sidebar-handleBackgroundColor);
            font-size: 18px;
            padding-top: 1rem;
            padding-bottom: 1rem;
            border: 1px solid var(--sidebar-handleBorderColor);
            border-right: none;

            svg {
                position: relative;
                top: -2px;
            }
        }

        &__transform {
            font-size: 12px;
        }

        &__transform-footer {
            display: flex;
            justify-content: flex-end;

            > div, > button {
                margin: .5rem;
            }
        }

        &__textarea {
            max-width: unset;
            margin-bottom: .5rem;

            &.graphql {
                height: 20rem;
                font-family: 'Courier New', Courier, monospace;
            }

            &.graphql-variables {
                height: 8rem;
                font-family: 'Courier New', Courier, monospace;
            }
        }
    } // .ddMapping

.rich-html-editor--code {
    padding: 0;
    height: 300px;

    .ace_gutter-cell.ace_error {
        background-position: 0.125em 0.1375em;
    }

    .ace_print-margin {
        display: none;
    }

    .#{ $is-expanded } & {
        height: 100%;
    }
}

.js-editor {
    text-transform: none;

    &.#{ $is-expanded } {
        position: fixed;
        top: 0;
        right: 0;
        left: -8%;
        bottom: 0;
        z-index: 250;
    }

    &__toolbar {
        display: flex;
        align-items: center;
        color: var(--modal-headerColor);
        background-image: linear-gradient(var(--modal-headerBackgroundColorTop), var(--modal-headerBackgroundColorBottom));
        padding: 5px;
        padding: 15px 25px;
    }

    &__title {
        font-size: 1.25rem;
        font-weight: 600;
    }

    &__dropdown {
        font-weight: 600;
        border-color: rgba(255, 255, 255, 0.5);
        margin-left: auto;
        width: 15em;
    }

    &__close {
        font-size: 1.75rem;
        margin-left: 3.5rem;
    }
}

.select--open {
    width: 100%;
    height: 200px;
    color: var(--form-inputColor);
    background-color: var(--form-inputBackgroundColor);
    font-size: 0.8125rem;

    option{
        padding: 8px 4px;

        :hover {
            cursor: pointer;
            color: var(--form-inputHoverColor);
            background-color: var(--form-inputHoverBackgroundColor);
        }
    }
}

.name-value {
    &__section {
        width: 100%;
        margin-bottom: 0.75em;
    }

    &__header {
        display: flex;
        margin-bottom: 0.75em;
    }

    &__label {
        display: flex;
        flex-direction: row;
        align-items: center;
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--form-labelColor);
    }

    &__item {
        display: flex;

        &:not(:first-of-type) {
            margin-top: 0.75rem;
        }
    }

    &__col {
        display: flex;

        &:first-child {
            width: 50%;
            padding-right: 0.5rem;
        }

        &:not(:first-child):not(:last-child) {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
        }

        &:last-child {
            width: 50%;
            padding-left: 0.5rem;
        }

        > input {
            width: 100%;
        }
    }

    &__delete {
        margin-left: 10px;
    }

    &__input {
        @extend %input;
        width: 8em;
    }

    &__add {
        @extend %button-alt;
        @extend %button-compact;
        margin-top: 1.75rem;
    }
}

</style>
